import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Building2,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Calendar,
  TrendingUp,
  ArrowLeft,
  ClipboardList,
  Loader2,
  Briefcase,
  Bath,
  Warehouse,
  QrCode,
  RefreshCw
} from "lucide-react";

const Dashboard = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [qrResult, setQrResult] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateQR = async () => {
    setRegenerating(true);
    setQrResult(null);
    try {
      const response = await axios.post(`${API}/regenerate-all-qr?base_url=${encodeURIComponent(window.location.origin)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrResult(response.data);
      toast.success("تم إعادة توليد جميع رموز QR بنجاح");
    } catch (error) {
      toast.error(error.response?.data?.detail || "فشل في إعادة توليد رموز QR");
    } finally {
      setRegenerating(false);
    }
  };

  const handleResetData = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/reset-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم تصفير جميع البيانات التشغيلية بنجاح");
      setResetConfirm(false);
      fetchStats();
    } catch (error) {
      toast.error("فشل في تصفير البيانات");
    } finally {
      setResetting(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "قيد الانتظار",
      scheduled: "مجدول",
      in_progress: "قيد التنفيذ",
      completed: "مكتمل",
      cancelled: "ملغي"
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      low: "منخفضة",
      medium: "متوسطة",
      high: "عالية",
      urgent: "عاجلة"
    };
    return labels[priority] || priority;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader">
          <img src="/lucent-logo.png" alt="LUCENT" />
          <div className="lucent-loader-bar"></div>
        </div>
      </div>
    );
  }

  // الصف الأول: المواقع - مفلترة حسب الدور
  const allLocationCards = [
    {
      key: "clinics",
      title: "إجمالي العيادات",
      value: stats?.total_clinics || 0,
      icon: Building2,
      color: "bg-[#F3F2FA]",
      iconColor: "text-[#655dc6]",
      link: "/clinics"
    },
    {
      key: "offices",
      title: "إجمالي المكاتب",
      value: stats?.total_offices || 0,
      icon: Briefcase,
      color: "bg-blue-50",
      iconColor: "text-blue-600",
      link: "/offices"
    },
    {
      key: "restrooms",
      title: "إجمالي دورات المياه",
      value: stats?.total_restrooms || 0,
      icon: Bath,
      color: "bg-cyan-50",
      iconColor: "text-cyan-600",
      link: "/restrooms"
    },
    {
      key: "facilities",
      title: "إجمالي المرافق",
      value: stats?.total_facilities || 0,
      icon: Warehouse,
      color: "bg-teal-50",
      iconColor: "text-teal-600",
      link: "/facilities"
    }
  ];

  // فلترة بطاقات المواقع حسب الدور
  const roleLocationFilter = {
    medical_manager: ["clinics"],
    it_manager: ["clinics", "offices", "facilities"],
  };
  const allowedLocations = roleLocationFilter[user?.role];
  const locationCards = allowedLocations
    ? allLocationCards.filter(c => allowedLocations.includes(c.key))
    : allLocationCards;

  // الصف الثاني: الطلبات
  const requestCards = [
    {
      title: "طلبات قيد الانتظار",
      value: stats?.pending_requests || 0,
      icon: Clock,
      color: "bg-amber-100",
      iconColor: "text-amber-600"
    },
    {
      title: "طلبات قيد التنفيذ",
      value: stats?.in_progress_requests || 0,
      icon: Wrench,
      color: "bg-orange-100",
      iconColor: "text-orange-600"
    },
    {
      title: "طلبات مجدولة",
      value: stats?.scheduled_requests || 0,
      icon: Calendar,
      color: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      title: "طلبات مكتملة",
      value: stats?.completed_requests || 0,
      icon: CheckCircle2,
      color: "bg-emerald-100",
      iconColor: "text-emerald-600"
    }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight">مرحباً، {user?.full_name}</h1>
          <p className="text-gray-500 mt-1.5">إليك ملخص حالة الصيانة اليوم</p>
        </div>
        <div className="flex gap-2">
          <Link to="/maintenance">
            <Button className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="view-requests-btn">
              عرض جميع الطلبات
              <ArrowLeft className="w-4 h-4 mr-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* الصف الأول: المواقع */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {locationCards.map((card, index) => (
          <Link key={index} to={card.link}>
            <Card className="card-hover border border-gray-200 shadow-none rounded-xl cursor-pointer" data-testid={`location-card-${index}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1.5">{card.title}</p>
                    <p className="text-4xl font-light tracking-tighter text-[#0A0A0A]">{card.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center`}>
                    <card.icon className={`w-6 h-6 ${card.iconColor}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* الصف الثاني: الطلبات */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {requestCards.map((card, index) => (
          <Card key={index} className="card-hover border border-gray-200 shadow-none rounded-xl" data-testid={`request-card-${index}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1.5">{card.title}</p>
                  <p className="text-4xl font-light tracking-tighter text-[#0A0A0A]">{card.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center`}>
                  <card.icon className={`w-6 h-6 ${card.iconColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Alerts */}
      {(stats?.urgent_requests > 0 || stats?.high_priority_requests > 0) && (
        <Card className="border-red-200 bg-red-50" data-testid="alerts-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-red-800">تنبيهات هامة</h3>
                <p className="text-sm text-red-600">
                  يوجد {stats?.urgent_requests || 0} طلب عاجل و {stats?.high_priority_requests || 0} طلب عالي الأولوية يحتاج للمعالجة
                </p>
              </div>
              <Link to="/maintenance?priority=urgent" className="mr-auto">
                <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
                  عرض الطلبات
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Requests & Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Requests */}
        <Card className="border border-gray-200 shadow-none rounded-xl" data-testid="recent-requests-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-[#0A0A0A] flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#655dc6]" />
              آخر الطلبات
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_requests?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_requests.map((request, index) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full" style={{
                        backgroundColor: request.priority === 'urgent' ? '#EF4444' :
                          request.priority === 'high' ? '#F97316' :
                          request.priority === 'medium' ? '#F59E0B' : '#3B82F6'
                      }} />
                      <div>
                        <p className="font-semibold text-[#0A0A0A] text-sm">{request.clinic_name}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]">{request.description}</p>
                      </div>
                    </div>
                    <Badge className={`status-${request.status}`}>
                      {getStatusLabel(request.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>لا توجد طلبات صيانة حالياً</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Requests by Category */}
        <Card className="border border-gray-200 shadow-none rounded-xl" data-testid="categories-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-[#0A0A0A] flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#655dc6]" />
              الطلبات حسب الفئة
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.requests_by_category && Object.keys(stats.requests_by_category).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.requests_by_category).map(([category, count]) => {
                  const categoryLabels = {
                    wall: "الجدران",
                    floor: "الأرضيات",
                    ceiling: "السقف",
                    lighting: "الإضاءة",
                    plumbing: "السباكة",
                    electrical: "الكهرباء",
                    dental_chair: "كرسي الأسنان",
                    xray: "جهاز الأشعة",
                    sterilizer: "جهاز التعقيم",
                    compressor: "الضاغط",
                    suction: "جهاز الشفط",
                    medical_equipment: "أجهزة طبية",
                    it_equipment: "جهاز إلكتروني",
                    other: "أخرى"
                  };
                  const maxCount = Math.max(...Object.values(stats.requests_by_category));
                  const percentage = (count / maxCount) * 100;
                  const catLabel = category.startsWith("biomed:") ? "جهاز طبي" : (categoryLabels[category] || category);
                  
                  return (
                    <div key={category} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{catLabel}</span>
                        <span className="font-semibold text-[#0A0A0A]">{count}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#655dc6] rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>لا توجد بيانات متاحة</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions - مخفية عن المدير الطبي ومدير تقنية المعلومات */}
      {user?.role !== "medical_manager" && user?.role !== "it_manager" && (
      <Card className="border-0 shadow-none rounded-2xl bg-[#655dc6]" data-testid="quick-actions-card">
        <CardContent className="py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-white text-center sm:text-right">
              <h3 className="text-xl font-bold mb-2">إجراءات سريعة</h3>
              <p className="text-white/70">ابدأ بتقييم العيادات أو مسح رمز QR للإبلاغ عن مشكلة</p>
            </div>
            <div className="flex gap-3">
              <Link to="/clinics">
                <Button className="bg-white text-[#655dc6] hover:bg-slate-100" data-testid="view-clinics-btn">
                  <Building2 className="w-4 h-4 ml-2" />
                  عرض العيادات
                </Button>
              </Link>
              <Link to="/qr-scanner">
                <Button className="bg-[#9b8fd4] hover:bg-[#8B6BA8] text-white" data-testid="scan-qr-btn">
                  <Wrench className="w-4 h-4 ml-2" />
                  إبلاغ عن صيانة
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* QR Regeneration Dialog - Admin Only */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              إعادة توليد رموز QR
            </DialogTitle>
            <DialogDescription>
              سيتم إعادة توليد جميع رموز QR لتشير إلى الرابط الحالي للنظام
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">تنبيه هام</p>
                  <p className="text-sm text-amber-700 mt-1">
                    بعد إعادة التوليد، ستحتاج لطباعة رموز QR الجديدة ووضعها في المواقع المختلفة.
                  </p>
                </div>
              </div>
            </div>

            {qrResult && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <p className="font-medium text-emerald-800">تم بنجاح!</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-emerald-700">
                  <p>العيادات: {qrResult.details?.clinics || 0}</p>
                  <p>المكاتب: {qrResult.details?.offices || 0}</p>
                  <p>دورات المياه: {qrResult.details?.restrooms || 0}</p>
                  <p>المرافق: {qrResult.details?.facilities || 0}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleRegenerateQR}
                className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                disabled={regenerating}
                data-testid="confirm-regenerate-qr-btn"
              >
                {regenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin ml-2" />
                    جاري إعادة التوليد...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 ml-2" />
                    إعادة توليد الآن
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setQrDialogOpen(false);
                  setQrResult(null);
                }}
                className="flex-1"
              >
                إغلاق
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Data Confirmation Dialog */}
      <Dialog open={resetConfirm} onOpenChange={setResetConfirm}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-red-600">تصفير البيانات التشغيلية</DialogTitle>
            <DialogDescription className="text-right">
              سيتم حذف جميع البيانات التالية نهائياً:
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 rounded-lg p-4 text-sm text-red-700 space-y-1">
            <p>• طلبات الصيانة</p>
            <p>• الاقتراحات والشكاوى</p>
            <p>• التنبيهات الذكية</p>
            <p>• التقييمات الدورية</p>
            <p>• طلبات المستلزمات</p>
          </div>
          <p className="text-sm text-slate-500">سيتم الإبقاء على: المستخدمين، العيادات، المكاتب، المرافق، دورات المياه</p>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setResetConfirm(false)} className="flex-1">إلغاء</Button>
            <Button onClick={handleResetData} disabled={resetting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reset-btn">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : "تأكيد التصفير"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
